import { Component, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { JjamesButtonComponent, JJamesPaginationConfig, JJamesPaginatorComponent, printReceiptSlip } from 'shared-lib';
import {
    EnrichedPaymentSessionDto,
    PaymentProviderKind,
    PaymentRevenueStatsDto,
    PaymentService,
    PaymentStatus
} from '../../../api/gen';

/**
 * Money actually taken, across every rail.
 *
 * The obligations list next door shows debts the late-payment sweep raised — money that *went wrong*.
 * Most revenue never becomes an obligation: it is paid at the column or on the parker's phone before
 * anything is owed, and until this view existed none of it appeared on the operator dashboard at all.
 * A free-flow site could take a day of card payments and read €0.00 collected.
 *
 * Read-only on purpose. Refund and cancel exist on the API and are deliberately not wired here yet —
 * both move real money and deserve the same reason-and-confirm treatment the obligation actions have,
 * rather than a button on a list.
 */
/** What the receipt dialog shows: the stored printout plus the stay it paid for. */
interface ReceiptView {
    titleKey: string;
    icon: string;
    text: string;
    plate?: string;
    spaceName?: string;
    zoneName?: string;
    entry?: string;
    exit?: string;
    stillParked: boolean;
    duration?: string;
    paidAt?: string;
    currency: string;
    /** Coins only: everything that went into the machine. */
    inserted?: number;
    netPaid: number;
    credited: number;
    terminalId?: string;
    /** Belegnummer — names the printed sheet and the saved PDF. */
    beleg?: string;
}

@Component({
    selector: 'app-payments-list',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        MatIconModule,
        MatProgressSpinnerModule,
        MatSnackBarModule,
        JjamesButtonComponent,
        JJamesPaginatorComponent,
        TranslatePipe
    ],
    templateUrl: './payments-list.component.html',
    styleUrl: './payments-list.component.scss'
})
export class PaymentsListComponent {
    rowStatusClass(payment: EnrichedPaymentSessionDto): string {
        if (this.isRefunded(payment)) return 'status-row-refunded';
        const status = (payment.status || '').toLowerCase();
        switch (status) {
            case 'paid': return 'status-row-paid';
            case 'failed': return 'status-row-failed';
            case 'refunded': return 'status-row-refunded';
            case 'cancelled':
            case 'canceled': return 'status-row-cancelled';
            case 'pending':
            case 'initiated': return 'status-row-pending';
            case 'expired': return 'status-row-expired';
            default: return '';
        }
    }

    statusBadgeClass(payment: EnrichedPaymentSessionDto): string {
        if (this.isRefunded(payment)) return 'bg-amber-100 text-amber-800 border-amber-300';
        const status = (payment.status || '').toLowerCase();
        switch (status) {
            case 'paid': return 'bg-emerald-100 text-emerald-800 border-emerald-300';
            case 'failed': return 'bg-rose-100 text-rose-800 border-rose-300';
            case 'refunded': return 'bg-amber-100 text-amber-800 border-amber-300';
            case 'cancelled':
            case 'canceled': return 'bg-slate-100 text-slate-700 border-slate-300';
            case 'pending':
            case 'initiated': return 'bg-blue-100 text-blue-800 border-blue-300';
            case 'expired': return 'bg-red-100 text-red-800 border-red-300';
            default: return 'bg-slate-100 text-slate-700 border-slate-300';
        }
    }
    /**
     * Site narrowing, owned by the page around this tab so both tabs answer about the same site.
     *
     * Empty means every site the caller may see — which the server decides from their token, not from
     * this input. Passing a space here can only narrow that set.
     */
    readonly parkingSpaceId = input<string>('');

    /** Rows one export may contain — same ceiling as the obligations export, for the same reason. */
    private static readonly MaxExportRows = 20_000;
    private static readonly ExportPageSize = 200;

    private paymentService = inject(PaymentService);
    private snackBar = inject(MatSnackBar);
    private translate = inject(TranslateService);

    payments = signal<EnrichedPaymentSessionDto[]>([]);
    stats = signal<PaymentRevenueStatsDto | undefined>(undefined);
    isLoading = signal(false);
    isExporting = signal(false);
    statsFailed = signal(false);
    totalCount = signal(0);

    providerFilter = signal<PaymentProviderKind | ''>('');
    statusFilter = signal<PaymentStatus | ''>('');
    plateFilter = signal('');

    /** 1-based on this endpoint, unlike the obligations list. Kept 0-based here and converted on send. */
    pageIndex = signal(0);
    pageSize = signal(20);

    readonly providers: PaymentProviderKind[] = [
        PaymentProviderKind.Stripe,
        PaymentProviderKind.CardTerminal,
        PaymentProviderKind.Im30,
        PaymentProviderKind.CoinAcceptor,
        PaymentProviderKind.Wallet
    ];

    readonly statuses: PaymentStatus[] = [
        PaymentStatus.Initiated,
        PaymentStatus.Pending,
        PaymentStatus.Paid,
        PaymentStatus.Failed,
        PaymentStatus.Cancelled,
        PaymentStatus.Refunded,
        PaymentStatus.Expired
    ];

    readonly statsLoaded = computed(() => this.stats() !== undefined);
    readonly netRevenue = computed(() => this.stats()?.netRevenue ?? 0);
    readonly chargedAmount = computed(() => this.stats()?.chargedAmount ?? 0);
    readonly refundedAmount = computed(() => this.stats()?.refundedAmount ?? 0);
    readonly pendingAmount = computed(() => this.stats()?.pendingAmount ?? 0);
    readonly hasMixedCurrencies = computed(() => this.stats()?.hasMixedCurrencies === true);

    readonly currencyCode = computed(() => {
        const code = this.stats()?.currency;
        return code && code.length > 0 ? code : 'EUR';
    });

    /**
     * Net revenue per rail, only for rails that have actually taken money.
     *
     * The server returns every rail including zeros — correct for a chart axis, noise in a summary
     * strip on a site that has only ever used one of them.
     */
    readonly revenueByRail = computed(() => {
        const byKind = this.stats()?.byProviderKind ?? {};
        const counts = this.stats()?.countByProviderKind ?? {};
        return this.providers
            .map(kind => ({ kind, amount: byKind[kind] ?? 0, count: counts[kind] ?? 0 }))
            .filter(row => row.amount !== 0 || row.count > 0);
    });

    readonly pagination = computed<JJamesPaginationConfig>(() => ({
        currentPage: this.pageIndex(),
        totalPages: Math.max(1, Math.ceil(this.totalCount() / this.pageSize())),
        pageSize: this.pageSize(),
        totalItems: this.totalCount(),
        itemsPerPage: this.pageSize(),
        pageSizeOptions: [10, 20, 50, 100]
    }));

    /**
     * Loads on creation and again whenever the selected site changes.
     *
     * An effect rather than `ngOnInit` plus a setter: the two would race on the first change
     * detection and fire two identical requests, and the totals shown would be whichever landed last.
     *
     * The work is `untracked` on purpose, and it is load-bearing. `load()` reads the provider, status,
     * plate and paging signals, so tracking them would make this effect fire on every keystroke in the
     * plate box — and, worse, re-run after `onPageChange` and reset `pageIndex` to 0, so page two
     * could never be reached. The site is the only thing this reacts to.
     */
    private readonly reloadOnSiteChange = effect(() => {
        this.parkingSpaceId();

        untracked(() => {
            this.pageIndex.set(0);
            this.load();
            this.loadStats();
        });
    });

    load(): void {
        this.isLoading.set(true);

        this.paymentService
            .paymentsEnrichedGet(
                this.providerFilter() || undefined,
                // Separators are stripped server-side, so "W-1234 X" finds the same stays as "W1234X".
                this.plateFilter().trim() || undefined,
                this.statusFilter() || undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                this.pageIndex() + 1,
                this.pageSize(),
                this.parkingSpaceId() || undefined
            )
            .subscribe({
                next: (result) => {
                    this.payments.set(result?.items ?? []);
                    this.totalCount.set(result?.totalCount ?? 0);
                    this.isLoading.set(false);
                },
                error: (err) => {
                    console.error('Failed to load payments', err);
                    this.payments.set([]);
                    this.totalCount.set(0);
                    this.isLoading.set(false);
                    this.snackBar.open(this.translate.instant('payments.loadFailed'), 'Dismiss', { duration: 5000 });
                }
            });
    }

    /**
     * Everything the caller may see, or one site of it — and deliberately not narrowed by the row
     * filters. A total that moves with the table's filter is not a total; the site selector is a
     * different thing, because it changes which estate the question is about.
     */
    private loadStats(): void {
        this.paymentService
            .paymentsStatsGet(undefined, undefined, undefined, this.parkingSpaceId() || undefined)
            .subscribe({
                next: (result) => {
                    this.stats.set(result);
                    this.statsFailed.set(false);
                },
                error: (err) => {
                    // Dash, not zeros: unreachable revenue is not €0.00 taken.
                    console.error('Failed to load revenue statistics', err);
                    this.stats.set(undefined);
                    this.statsFailed.set(true);
                }
            });
    }

    applyFilters(): void {
        this.pageIndex.set(0);
        this.load();
    }

    clearFilters(): void {
        this.providerFilter.set('');
        this.statusFilter.set('');
        this.plateFilter.set('');
        this.applyFilters();
    }

    onPageChange(event: { pageIndex: number; pageSize: number }): void {
        this.pageIndex.set(event.pageIndex);
        this.pageSize.set(event.pageSize);
        this.load();
    }

    /**
     * What this payment is actually worth, as the server computed it.
     *
     * Deliberately not recomputed here from `transactions`: the amount a session was opened for stops
     * being the truth the moment a refund lands, and a refunded session can still read `Paid`. One
     * definition of "net paid", on the server, is what keeps this column and the revenue tile agreeing.
     */
    netPaid(payment: EnrichedPaymentSessionDto): number {
        return payment.netPaidAmount ?? 0;
    }

    isRefunded(payment: EnrichedPaymentSessionDto): boolean {
        return payment.hasRefund === true;
    }

    /**
     * What was actually taken. On a coin row the session amount is what the kiosk asked for — a
     * cancelled coin session asked for 3.20 and took 2.00 — so a paid row shows the money itself.
     */
    amountShown(payment: EnrichedPaymentSessionDto): number {
        return payment.status === PaymentStatus.Paid
            ? payment.chargedAmount ?? payment.amount ?? 0
            : payment.amount ?? 0;
    }

    /** Coins that went to the parker's wallet rather than the stay. Zero for every other rail. */
    walletCredit(payment: EnrichedPaymentSessionDto): number {
        return payment.walletCreditAmount ?? 0;
    }

    /** Which physical thing took the money, for the rail column. */
    railKey(payment: EnrichedPaymentSessionDto): string {
        return `payments.rail_${payment.providerKind ?? 'Unknown'}`;
    }

    /**
     * Stay length as "2 h 15 min".
     *
     * The server sends a .NET TimeSpan, which serialises as "d.hh:mm:ss" (or "hh:mm:ss" under a day) —
     * not something to put in front of an operator. Null stays null: an open stay has no duration yet,
     * and rendering "0 min" would claim it does.
     */
    duration(payment: EnrichedPaymentSessionDto): string | null {
        const raw = payment.stayDuration;
        if (!raw) return null;

        const match = /^(?:(\d+)\.)?(\d{2}):(\d{2}):(\d{2})/.exec(raw);
        if (!match) return null;

        const days = Number(match[1] ?? 0);
        const hours = days * 24 + Number(match[2]);
        const minutes = Number(match[3]);

        return hours > 0 ? `${hours} h ${minutes} min` : `${minutes} min`;
    }

    readonly selectedReceipt = signal<ReceiptView | null>(null);

    getReceiptInfo(payment: EnrichedPaymentSessionDto): {
        labelKey: string;
        icon: string;
        buttonClass: string;
        tooltip: string;
        url?: string;
        isExternal: boolean;
    } | null {
        const receiptUrl = payment.receiptUrl || payment.terminalCapture?.fiscalReceiptUrl;
        const isFiscal = payment.isFiscal ?? payment.terminalCapture?.fiscalRegistered ?? (receiptUrl?.includes('efsta'));
        const receiptText = this.receiptText(payment);

        if (receiptUrl) {
            if (isFiscal || receiptUrl.includes('efsta')) {
                return {
                    labelKey: 'payments.receiptEfsta',
                    icon: 'verified',
                    buttonClass: 'bg-emerald-50 text-emerald-800 border-emerald-300 hover:bg-emerald-100/90 shadow-2xs',
                    tooltip: 'EFSTA Fiscal Receipt (RKSV Compliant)',
                    url: receiptUrl,
                    isExternal: true
                };
            }
            if (receiptUrl.includes('stripe')) {
                return {
                    labelKey: 'payments.receiptStripe',
                    icon: 'receipt_long',
                    buttonClass: 'bg-blue-50 text-blue-800 border-blue-300 hover:bg-blue-100/90 shadow-2xs',
                    tooltip: 'Stripe Payment Receipt',
                    url: receiptUrl,
                    isExternal: true
                };
            }
            return {
                labelKey: 'payments.receiptInvoice',
                icon: 'description',
                buttonClass: 'bg-indigo-50 text-indigo-800 border-indigo-300 hover:bg-indigo-100/90 shadow-2xs',
                tooltip: 'Digital Receipt / Invoice',
                url: receiptUrl,
                isExternal: true
            };
        }

        if (receiptText) {
            const kind = this.slipKind(payment);
            return {
                labelKey: kind.buttonKey,
                icon: kind.icon,
                buttonClass: 'bg-slate-100 text-slate-800 border-slate-300 hover:bg-slate-200/80 shadow-2xs',
                tooltip: 'The printout stored with this payment',
                isExternal: false
            };
        }

        // Deliberately nothing for a Paid row with neither a URL nor a slip.
        //
        // This used to return a "Stripe Receipt" / "Terminal / EFSTA" button with no url and no
        // text, purely because the row was Paid. openReceipt() then found nothing to open and
        // answered with a snackbar — a button whose only behaviour was to apologise. It reads as a
        // broken link, and it hides the real cause: the backend populates ReceiptUrl solely from
        // TerminalCapture.FiscalReceiptUrl, so a Stripe session (which has no capture) can never
        // have one. Stripe's receipt lives on the Charge and we do not store it yet.
        return null;
    }

    /**
     * Whether the row also has a printout behind its fiscal bill. A coin or card row with an EFSTA
     * bill has both, and the main button opens the bill — this is the way to the coin list or slip.
     */
    hasSlipBehindBill(payment: EnrichedPaymentSessionDto): boolean {
        return !!this.receiptText(payment) && this.getReceiptInfo(payment)?.isExternal === true;
    }

    openReceipt(payment: EnrichedPaymentSessionDto, event?: Event): void {
        event?.stopPropagation();
        const receiptUrl = payment.receiptUrl || payment.terminalCapture?.fiscalReceiptUrl;

        if (receiptUrl) {
            window.open(receiptUrl, '_blank', 'noopener,noreferrer');
            return;
        }

        if (this.receiptText(payment)) {
            this.showTerminalSlip(payment);
            return;
        }

        this.snackBar.open('No digital receipt available for this payment session.', 'Close', { duration: 3000 });
    }

    /**
     * Always shows the printout stored with the payment — never the fiscal bill.
     *
     * openReceipt() checks receiptUrl first and returns, so on a capture that HAS an EFSTA bill it
     * opened the bill and the slip was unreachable: two differently-labelled buttons, one outcome.
     * These are two different documents. The EFSTA bill is the fiscal record; this is the card
     * receipt (Händlerbeleg / Kundenbeleg) with the Beleg-Nr, masked PAN, GEN.NR. and amount — the
     * one the acquirer asks for when a cardholder disputes a charge — or the coin list for coins.
     *
     * The stay comes with it: a slip on its own names a terminal and a time, and an operator answering
     * a parker needs the plate, the site and when the car came and went.
     */
    showTerminalSlip(payment: EnrichedPaymentSessionDto, event?: Event): void {
        event?.stopPropagation();

        const text = this.receiptText(payment);
        if (!text) {
            this.snackBar.open(this.translate.instant('payments.receiptNoText'), 'Close', { duration: 3000 });
            return;
        }

        const kind = this.slipKind(payment);
        this.selectedReceipt.set({
            titleKey: kind.titleKey,
            icon: kind.icon,
            text,
            plate: payment.licensePlate || undefined,
            spaceName: payment.parkingSpaceName || undefined,
            zoneName: payment.parkingZoneName || undefined,
            entry: payment.stayStartedAt || undefined,
            exit: payment.stayEndedAt || undefined,
            stillParked: !!payment.stayStartedAt && !payment.stayEndedAt,
            duration: this.duration(payment) || undefined,
            paidAt: payment.paidAt || payment.createdAt || undefined,
            currency: payment.currency || 'EUR',
            inserted: payment.providerKind === PaymentProviderKind.CoinAcceptor ? this.amountShown(payment) : undefined,
            netPaid: this.netPaid(payment),
            credited: this.walletCredit(payment),
            terminalId: payment.terminalCapture?.paymentTerminalId || undefined,
            beleg: payment.terminalCapture?.externalPaymentId || undefined
        });
    }

    closeReceiptModal(): void {
        this.selectedReceipt.set(null);
    }

    /** Prints the open slip — see {@link printReceiptSlip} for why it goes through an iframe. */
    printReceiptModal(): void {
        const receipt = this.selectedReceipt();
        if (!receipt?.text) return;
        printReceiptSlip({
            text: `${this.stayBlock(receipt)}\n\n${receipt.text}`,
            title: this.translate.instant(receipt.titleKey),
            beleg: receipt.beleg,
            date: receipt.paidAt ? new Date(receipt.paidAt).toLocaleString() : undefined,
            plate: receipt.plate
        });
    }

    /**
     * The stay printed above the slip, aligned in the slip's own monospace — the sheet has to answer
     * "which car, where, when" without the screen next to it.
     */
    private stayBlock(receipt: ReceiptView): string {
        const t = (key: string) => this.translate.instant(key) as string;
        const when = (iso?: string) => iso ? new Date(iso).toLocaleString() : '';
        const rows: [string, string | undefined][] = [
            [t('payments.colPlate'), receipt.plate],
            [t('payments.colSpace'), receipt.spaceName],
            [t('payments.receiptZone'), receipt.zoneName],
            [t('payments.receiptEntry'), when(receipt.entry)],
            [t('payments.receiptExit'), receipt.stillParked ? t('payments.stayOpen') : when(receipt.exit)],
            [t('payments.receiptDuration'), receipt.duration]
        ];
        const shown = rows.filter((row): row is [string, string] => !!row[1]);
        if (shown.length === 0) return '';
        const width = Math.max(...shown.map(([label]) => label.length)) + 2;
        return shown.map(([label, value]) => label.padEnd(width) + value).join('\n');
    }

    private receiptText(payment: EnrichedPaymentSessionDto): string | undefined {
        return payment.terminalCapture?.receiptText || payment.receiptText || undefined;
    }

    private slipKind(payment: EnrichedPaymentSessionDto): { buttonKey: string; titleKey: string; icon: string } {
        switch (payment.providerKind) {
            case PaymentProviderKind.CoinAcceptor:
                return { buttonKey: 'payments.receiptCoins', titleKey: 'payments.receiptTitleCoins', icon: 'toll' };
            case PaymentProviderKind.Wallet:
                return { buttonKey: 'payments.receiptCredit', titleKey: 'payments.receiptTitleCredit', icon: 'account_balance_wallet' };
            default:
                return { buttonKey: 'payments.receiptCard', titleKey: 'payments.receiptTitleCard', icon: 'credit_card' };
        }
    }

    copyText(text: string, label: string): void {
        navigator.clipboard.writeText(text);
        this.snackBar.open(`${label} copied to clipboard`, '', { duration: 2000 });
    }

    async exportCsv(): Promise<void> {
        if (this.isExporting()) return;

        if (this.payments().length === 0) {
            this.snackBar.open(this.translate.instant('payments.exportEmpty'), 'Dismiss', { duration: 4000 });
            return;
        }

        this.isExporting.set(true);
        try {
            const rows = await this.fetchAllForExport();
            this.download(this.toCsv(rows));

            const truncated = rows.length >= PaymentsListComponent.MaxExportRows;
            this.snackBar.open(
                truncated
                    ? this.translate.instant('payments.exportTruncated', { exported: rows.length })
                    : this.translate.instant('payments.exportDone', { count: rows.length }),
                'Close',
                { duration: truncated ? 9000 : 4000 }
            );
        } catch (err) {
            console.error('Failed to export payments', err);
            this.snackBar.open(this.translate.instant('payments.exportFailed'), 'Dismiss', { duration: 6000 });
        } finally {
            this.isExporting.set(false);
        }
    }

    private async fetchAllForExport(): Promise<EnrichedPaymentSessionDto[]> {
        const collected: EnrichedPaymentSessionDto[] = [];

        for (let page = 1; collected.length < PaymentsListComponent.MaxExportRows; page++) {
            const result = await firstValueFrom(this.paymentService.paymentsEnrichedGet(
                this.providerFilter() || undefined,
                this.plateFilter().trim() || undefined,
                this.statusFilter() || undefined,
                undefined,
                undefined,
                undefined,
                undefined,
                page,
                PaymentsListComponent.ExportPageSize,
                this.parkingSpaceId() || undefined
            ));

            const items = result?.items ?? [];
            collected.push(...items);

            // A short page means the end, whatever the reported total says.
            if (items.length < PaymentsListComponent.ExportPageSize) break;
        }

        return collected.slice(0, PaymentsListComponent.MaxExportRows);
    }

    private toCsv(rows: EnrichedPaymentSessionDto[]): string {
        const headers = [
            'Created At', 'Paid At', 'Rail', 'Status', 'Licence Plate', 'Parking Space',
            'Entry', 'Exit', 'Amount', 'Net Paid', 'Currency',
            'Parking Stay ID', 'Payment Session ID'
        ];

        const quote = (value: unknown) => `"${String(value ?? '').replace(/"/g, '""')}"`;

        const lines = rows.map(p => [
            quote(p.createdAt),
            quote(p.paidAt),
            quote(p.providerKind),
            quote(p.status),
            quote(p.licensePlate),
            quote(p.parkingSpaceName),
            quote(p.stayStartedAt),
            quote(p.stayEndedAt),
            (p.amount ?? 0).toFixed(2),
            this.netPaid(p).toFixed(2),
            quote(p.currency || 'EUR'),
            quote(p.parkingProcessId),
            quote(p.id)
        ].join(','));

        return [headers.join(','), ...lines].join('\r\n');
    }

    private download(csv: string): void {
        // BOM so Excel does not mangle non-ASCII when read as the system codepage.
        const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `payments-${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }
}
